import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Box,
  Grid,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CardCommon from "../../../../components/card/CardCommon";

export interface VoidTransactionDetailsModalProps {
  setSelectedDate: (date: string) => void;
  selectedDate: string | Date | null;
}

// Constants
const DISPLAY_DATE_OPTIONS = {
  month: "short",
  day: "numeric",
} as const;

const PREVIOUS_DAYS_COUNT = 14;
const CARD_HEIGHT = "40px";

// Helper functions
const formatDisplayDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", DISPLAY_DATE_OPTIONS);
};

// Format date as YYYYMMDD for API (without hyphens)
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

// Generate exactly 14 days including today (today + previous 13 days) and sort DESC
const generateLast14Days = (): Date[] => {
  const days: Date[] = [];
  const today = new Date();

  for (let i = 13; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);
    date.setHours(0, 0, 0, 0);
    days.push(date);
  }

  return days.reverse();
};

const PrevDate: React.FC<VoidTransactionDetailsModalProps> = ({
  setSelectedDate,
  selectedDate,
}) => {
  const [previousDays, setPreviousDays] = useState<Date[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
console.log("Selected date in PrevDate component:", selectedDate);
  useEffect(() => {
    const days = generateLast14Days();
    setPreviousDays(days);
  }, []);

  const isDateSelected = useCallback(
    (date: Date): boolean => {
      if (!selectedDate) return false;
      const dateToCompare = formatDateForAPI(date);

      if (typeof selectedDate === "string") {
        return dateToCompare === selectedDate;
      }
      if (selectedDate instanceof Date) {
        return dateToCompare === formatDateForAPI(selectedDate);
      }
      return false;
    },
    [selectedDate],
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      const apiDate = formatDateForAPI(date);
      console.log("Selected date for API:", apiDate);
      setSelectedDate(apiDate);
    },
    [setSelectedDate],
  );

  const cardStyle = useMemo(
    () => ({
      height: CARD_HEIGHT,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "0 12px",
      whiteSpace: "nowrap" as const,
      width: "100%",
    }),
    [],
  );

  return (
    <Box style={{ width: "100%", marginBottom: "16px", marginTop: "16px" }}>
      <Grid container spacing={1}>
        {previousDays.map((date, index) => {
          const isSelected = isDateSelected(date);
          const displayDate = formatDisplayDate(date);

          return (
            <Grid
              item
              key={`date-${index}-${date.getTime()}`}
              onClick={() => handleDateClick(date)}
              style={{
                cursor: "pointer",
                flex: 1, // Use flex instead of xs for equal width
                minWidth: 0, // Allows flex items to shrink
              }}
            >
              <CardCommon color={isSelected ? "green" : ""} style={cardStyle}>
                <Typography
                  variant="body2"
                  style={{
                    textAlign: "center",
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? "#fff" : "inherit",
                    fontSize: isMobile ? "10px" : "12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayDate}
                </Typography>
              </CardCommon>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

PrevDate.displayName = "PrevDate";

export default PrevDate;
